import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Chip,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Check, Close, Edit } from "@mui/icons-material";
import { factory } from "../../../../../api/apiFactory";
import { parseSerials } from "../../../../../helpers/serials";
import { showNotification } from "../../../../../app/slices/notificationSlice";
import { HiOutlineHashtag } from "react-icons/hi2";
import FormDialog, { PlainCard } from "../../../../../components/FormDialog";

const productApi = factory.get("product");

// List of the serials of one product that one user currently holds; each can be edited inline.
const SerialsDialog = ({ row, onClose }) => {
  const dispatch = useDispatch();
  const [serials, setSerials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // { id, value }
  const [saving, setSaving] = useState(false);
  const { user_roles, permissions } = useSelector(
    (state) => state.user.userInfo
  );
  const canEdit =
    user_roles.some((r) => r === "super_admin" || r === "admin") ||
    permissions.some((p) => p.name === "serial_number_edit");

  useEffect(() => {
    if (!row) return;
    let cancelled = false;
    setEditing(null);
    setLoading(true);
    productApi
      .serials({ product_id: row.product_id, user_id: row.user_id })
      .then((res) => !cancelled && setSerials(parseSerials(res)))
      .catch(() => !cancelled && setSerials([]))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [row]);

  const saveSerial = () => {
    const value = editing.value.trim();
    if (!value) return;
    setSaving(true);
    productApi
      .updateSerial(editing.id, { serial_number: value })
      .then(() => {
        setSerials((prev) =>
          prev.map((s) =>
            s.id === editing.id ? { ...s, serial_number: value } : s,
          ),
        );
        setEditing(null);
        dispatch(showNotification({ message: "تم تعديل الرقم التسلسلي", type: "success" }));
      })
      .catch((err) =>
        dispatch(
          showNotification({
            message: err.response?.data?.errors?.[0] ?? "حدث خطأ",
            type: "error",
          }),
        ),
      )
      .finally(() => setSaving(false));
  };

  return (
    <FormDialog
      open={Boolean(row)}
      onClose={onClose}
      icon={<HiOutlineHashtag />}
      title="الارقام التسلسلية"
      subtitle={`${row?.product_name ?? ""} - ${row?.user_name ?? ""}`}
      loading={loading}
      keepMounted={false}
    >
      <PlainCard>
        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", rowGap: 1 }}>
          {serials.length > 0 ? (
            serials.map((serial) =>
              editing?.id === serial.id ? (
                <Stack key={serial.id} direction="row" alignItems="center">
                  <TextField
                    size="small"
                    autoFocus
                    value={editing.value}
                    disabled={saving}
                    onChange={(e) =>
                      setEditing({ ...editing, value: e.target.value })
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter") saveSerial();
                      if (e.key === "Escape") {
                        e.stopPropagation();
                        setEditing(null);
                      }
                    }}
                  />
                  <IconButton
                    size="small"
                    color="success"
                    disabled={saving || !editing.value.trim()}
                    onClick={saveSerial}
                  >
                    <Check fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    disabled={saving}
                    onClick={() => setEditing(null)}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Stack>
              ) : (
                <Chip
                  key={serial.id}
                  label={serial.serial_number}
                  deleteIcon={<Edit />}
                  onDelete={
                    canEdit
                      ? () =>
                          setEditing({ id: serial.id, value: String(serial.serial_number ?? "") })
                      : undefined
                  }
                />
              ),
            )
          ) : loading ? null : (
            <Typography variant="body2">لا توجد ارقام تسلسلية</Typography>
          )}
        </Stack>
      </PlainCard>
    </FormDialog>
  );
};

export default SerialsDialog;
